import { useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import cache, { CacheKeys, CacheTTL } from '../utils/cache';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * App-wide cached API hook. Use for GET requests to reduce DB reads and cost.
 * All GETs go through cache by default; pass forceRefresh to skip cache.
 */
export function useCachedApi() {
  const { token } = useAuth();

  const getConfig = useCallback(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}), [token]);

  /**
   * Cached GET request. Returns cached data if key exists and not expired.
   * @param {string} endpoint - e.g. '/api/patients' (relative to API_URL)
   * @param {string} cacheKey - from CacheKeys
   * @param {number} ttl - CacheTTL.SHORT | MEDIUM | LONG | VERY_LONG
   * @param {{ forceRefresh?: boolean, params?: object }} options
   */
  const cachedGet = useCallback(
    async (endpoint, cacheKey, ttl = CacheTTL.MEDIUM, options = {}) => {
      const { forceRefresh = false, params } = options;
      if (forceRefresh && cacheKey) cache.delete(cacheKey);
      const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
      const fn = async () => {
        const res = await axios.get(url, { ...getConfig(), params });
        return res.data;
      };
      if (cacheKey && !forceRefresh) return cache.getOrSet(cacheKey, fn, ttl, true);
      const data = await fn();
      if (cacheKey) cache.set(cacheKey, data, ttl);
      return data;
    },
    [getConfig]
  );

  /**
   * Invalidate one key or multiple by prefix.
   * @param {string} keyOrPrefix - exact key or prefix (e.g. 'patients_', 'student_')
   */
  const invalidate = useCallback((keyOrPrefix) => {
    const stats = cache.getStats();
    if (!keyOrPrefix) {
      cache.clear();
      return;
    }
    const isPrefix = keyOrPrefix.endsWith('_') || keyOrPrefix.endsWith('-');
    if (isPrefix) {
      stats.keys.forEach((k) => {
        if (k.startsWith(keyOrPrefix)) cache.delete(k);
      });
    } else {
      cache.delete(keyOrPrefix);
    }
  }, []);

  /**
   * Invalidate by type (convenience for common patterns).
   * @param {'patients'|'students'|'patient'|'appointments'|'reviews'|'admin'|'all'} type
   * @param {string} [id] - for 'patient' type: nationalId
   */
  const invalidateByType = useCallback((type, id) => {
    switch (type) {
      case 'patients':
        invalidate('patients_');
        invalidate('student_patients_');
        invalidate('dentist_patients_');
        invalidate('admin_patients_');
        break;
      case 'students':
        invalidate('students_');
        invalidate('supervisor_students_');
        invalidate('admin_students_');
        break;
      case 'patient':
        if (id) {
          cache.delete(CacheKeys.PATIENT_DETAILS(id));
          cache.delete(CacheKeys.PATIENT_PUBLIC(id));
          cache.delete(CacheKeys.PATIENT_PUBLIC_FULL(id));
          cache.delete(CacheKeys.PATIENT_APPOINTMENTS(id));
          cache.delete(CacheKeys.SUPERVISOR_PATIENT(id));
        }
        break;
      case 'appointments':
        invalidate('student_appointments_');
        invalidate('dentist_appointments_');
        invalidate('admin_appointments_');
        invalidate('patient_appointments_');
        break;
      case 'reviews':
        invalidate('supervisor_reviews_');
        invalidate('student_reviews_');
        invalidate('admin_reviews_');
        break;
      case 'admin':
        invalidate('admin_');
        break;
      case 'all':
        cache.clear();
        break;
      default:
        invalidate(type);
    }
  }, [invalidate]);

  return { cachedGet, invalidate, invalidateByType, getConfig };
}

export { CacheKeys, CacheTTL };
export default useCachedApi;
